import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const DIMS = [["day", "Per day"], ["source", "Source"], ["campaign", "Campaign"], ["site", "Site / placement"], ["device", "Device"], ["country", "Country"]];
const RANGES = [[1, "Today"], [7, "7 days"], [30, "30 days"], [365, "All"]];
const COLS = [["page_view", "Visits"], ["view_item", "Product view"], ["add_to_cart", "Add to cart"], ["begin_checkout", "Checkout"]];

const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "–");

const Pills = ({ items, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testPrefix}-${v}`}
        className={`border px-3 py-1.5 text-xs transition-colors ${value === v ? "border-cta bg-cta text-white" : "border-line text-zinc-300 hover:border-zinc-500"}`}>
        {label}
      </button>
    ))}
  </div>
);

// Unique sessions per funnel step, split by day / ad source / campaign / placement / device / country.
export const FunnelReport = ({ adminKey }) => {
  const [by, setBy] = useState("day");
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/funnel", { params: { by, days }, headers: { "X-Admin-Key": adminKey } })
      .then((r) => setRows(Array.isArray(r.data?.rows) ? r.data.rows : []))
      .catch(() => setRows([]));
  }, [by, days, adminKey]);

  return (
    <section className="mt-14" data-testid="admin-funnel">
      <h2 className="font-display text-2xl text-white">Funnel (unique sessions)</h2>
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:justify-between">
        <Pills items={DIMS} value={by} onChange={setBy} testPrefix="funnel-by" />
        <Pills items={RANGES} value={days} onChange={setDays} testPrefix="funnel-days" />
      </div>
      <div className="mt-5 overflow-x-auto border border-line">
        <table className="w-full min-w-[640px] text-left text-sm" data-testid="funnel-table">
          <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-zinc-400">
            <tr>
              <th className="px-4 py-3 font-normal">{DIMS.find(([v]) => v === by)?.[1]}</th>
              {COLS.map(([k, l]) => <th key={k} className="px-4 py-3 font-normal">{l}</th>)}
              <th className="px-4 py-3 font-normal">Cart → Checkout</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-zinc-400">No data for this range yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-line text-zinc-200" data-testid={`funnel-row-${r.key}`}>
                <td className="max-w-[220px] truncate px-4 py-3 text-white" title={r.key}>{r.key}</td>
                <td className="px-4 py-3">{r.page_view}</td>
                <td className="px-4 py-3">{r.view_item} <span className="text-xs text-zinc-400">{pct(r.view_item, r.page_view)}</span></td>
                <td className="px-4 py-3">{r.add_to_cart} <span className="text-xs text-zinc-400">{pct(r.add_to_cart, r.view_item)}</span></td>
                <td className="px-4 py-3">{r.begin_checkout} <span className="text-xs text-zinc-400">{pct(r.begin_checkout, r.view_item)}</span></td>
                <td className="px-4 py-3">{pct(r.begin_checkout, r.add_to_cart)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-zinc-400">Percentages: product view vs visits, cart and checkout vs product views. Purchases are counted in Shopify. Source / campaign / site come from the ad's UTM parameters (tracked from now on).</p>
    </section>
  );
};
